import classNames from "classnames";
import { FastAverageColor } from "fast-average-color";
import React, { type ImgHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";

interface IProps extends ImgHTMLAttributes<HTMLImageElement> {
  srcError?: string;
  isLazy?: boolean;
  width?: number;
  height?: number;
  className?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
}

export default function Image({ className, src = "", alt, width = 400, height = 500, loading = "lazy", draggable, ...props }: IProps) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [onLoad, setOnLoad] = useState(false);

  const pictureRef = useRef<HTMLPictureElement | null>(null);

  const handleObserver = useCallback(([entry]: IntersectionObserverEntry[]) => {
    if (entry.isIntersecting) {
      setIsIntersecting(true);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px 0px 40px 0px",
      threshold: 0.1,
    });

    if (pictureRef.current) observer.observe(pictureRef.current);

    return () => {
      observer.disconnect();
    };
  }, [handleObserver, pictureRef]);

  const isLazy = useMemo(() => loading === "lazy", [loading]);

  const [dominantColor, setDominantColor] = useState("rgb(255, 255, 255)");

  useEffect(() => {
    const fac = new FastAverageColor();

    if (!src || !isIntersecting) {
      return;
    }

    fac
      .getColorAsync(src, { mode: "speed", silent: true })
      .then((color) => {
        setDominantColor(color.rgb);
        setOnLoad(true);
      })
      .catch(() => {
        setDominantColor("rgb(255, 255, 255)");
      });
  }, [src, isIntersecting]);

  return (
    <picture
      className={classNames("relative block overflow-hidden", className)}
      ref={pictureRef}
      style={{
        aspectRatio: `${width} / ${height}`,
        backgroundColor: dominantColor,
      }}
    >
      {/* 빈 문자열 src 는 브라우저가 현재 페이지를 다시 내려받게 만든다 (React 경고) */}
      {!src ? null : isLazy ? (
        <img
          alt={alt}
          className={classNames(
            "dark:bg-dj-black size-full bg-white object-cover transition-opacity delay-300 duration-300",
            onLoad && isIntersecting ? "opacity-100" : "opacity-0",
          )}
          draggable={draggable}
          height={height}
          src={src}
          width={width}
          {...props}
        />
      ) : (
        <img alt={alt} className="size-full object-cover" loading="lazy" src={src} {...props} />
      )}
    </picture>
  );
}
